import type { ReactNode } from "react";

export type DataTableColumn<T> = {
  readonly className?: string;
  readonly header: ReactNode;
  readonly key: string;
  readonly render: (row: T) => ReactNode;
};

type DataTableProps<T> = {
  readonly columns: readonly DataTableColumn<T>[];
  readonly footer?: ReactNode;
  readonly header?: ReactNode;
  readonly rowKey: (row: T) => string;
  readonly rows: readonly T[];
};

export function DataTable<T>({
  columns,
  footer,
  header,
  rowKey,
  rows,
}: DataTableProps<T>) {
  return (
    <section className="overflow-hidden rounded-panel border border-border bg-surface">
      {header ? (
        <div className="flex items-center justify-between gap-3 border-b border-border px-3.5 py-3.5">
          {header}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-[13px]">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  className={`border-b border-border bg-panel-muted px-3.5 py-2.5 text-left text-[11px] font-extrabold uppercase tracking-[0.06em] text-ink-subtle ${column.className ?? ""}`}
                  key={column.key}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                className="group border-b border-border last:border-b-0 hover:bg-panel-muted"
                key={rowKey(row)}
              >
                {columns.map((column) => (
                  <td
                    className={`px-3.5 py-3.5 align-middle text-ink ${column.className ?? ""}`}
                    key={column.key}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3.5 py-2.5 text-xs text-ink-subtle">
          {footer}
        </div>
      ) : null}
    </section>
  );
}
